import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useImperativeHandle,
    forwardRef,
    useMemo,
} from 'react';
import {
    FormControl,
    Typography,
    Box,
    TextField,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    InputAdornment,
    Popover,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    IconButton,
    CircularProgress,
    Button,
} from '@mui/material';
import { CheckCircle, Search, Close, KeyboardArrowDown } from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindPopover,
} from 'material-ui-popup-state/hooks';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import {
    useFetchDataNoCacheQuery,
    usePersonalizePrepaidCardMutation,
    useUpdateCardStatusMutation,
} from '../../../services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { encrypt } from '../../../../../utils/encrypt';

export type ActivateCardFormHandle = {
    submit: () => void;
    reset: () => void;
};

export interface ActivateCardFormContentProps {
    /** Kart listesi sorgusu ve sıfırlama yalnızca true iken çalışır (ör. sekme: Kart Aktive Et). */
    isActive: boolean;
    onClose: () => void;
    onLoadingChange?: (loading: boolean) => void;
    /** Aktivasyon API başarılı olduktan sonra (ana modal kapanmadan hemen önce) — şifre modalı üst bileşende açılır. */
    onActivateSucceeded?: () => void;
    /** `add`: listede olmayan 16 karakterlik kart no (harf+rakam) için arama kutusunda "Kartı seç" gösterilir. */
    flowMode?: 'activate' | 'add';
    /** Kart Ekle akışında PersonalizePrepaidCard başarılı olunca (şifre modalı yok). */
    onAddCardSuccess?: () => void;
}

interface ActivateCardActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

// Filtre tanımı: cardType (P) ve status (T,B)
const cardFilters = [
    {
        field: 'cardType',
        logicOperator: 'and',
        values: ['P'],
        valueType: 1,
        operationType: 6,
    },
    {
        field: 'status',
        logicOperator: 'and',
        values: ['T,B'],
        valueType: 1,
        operationType: 11,
    },
];

/** CustomerCardsV2: düz `{ success, data: Card[], totalCount }` veya iç içe `{ data: { data, success, totalCount } }` */
function parseCustomerCardsV2Response(fetchData: any): {
    rows: any[];
    totalCount?: number;
    totalPages?: number;
    failed: boolean;
} {
    if (fetchData == null) {
        return { rows: [], failed: true };
    }

    if (fetchData.success === false) {
        return { rows: [], failed: true };
    }

    const rootData = fetchData.data;

    if (rootData === null || rootData === undefined) {
        return { rows: [], totalCount: fetchData.totalCount, totalPages: fetchData.totalPages, failed: fetchData.success === false };
    }

    if (Array.isArray(rootData)) {
        return {
            rows: rootData,
            totalCount: fetchData.totalCount,
            totalPages: fetchData.totalPages,
            failed: false,
        };
    }

    if (typeof rootData === 'object' && Array.isArray(rootData.data)) {
        if (rootData.success === false) {
            return { rows: [], failed: true };
        }
        return {
            rows: rootData.data,
            totalCount: rootData.totalCount ?? fetchData.totalCount,
            totalPages: rootData.totalPages ?? fetchData.totalPages,
            failed: false,
        };
    }

    return { rows: [], failed: fetchData.success === false };
}

export const ActivateCardFormContent = forwardRef<ActivateCardFormHandle, ActivateCardFormContentProps>(
    function ActivateCardFormContent(
        { isActive, onClose, onLoadingChange, onActivateSucceeded, onAddCardSuccess, flowMode = 'activate' },
        ref
    ) {
        const [selectedCard, setSelectedCard] = useState<any>(null);
        const [cardSearchText, setCardSearchText] = useState('');
        const [currentPage, setCurrentPage] = useState(1);
        const [allCards, setAllCards] = useState<any[]>([]);
        const [hasMore, setHasMore] = useState(true);
        const [isLoadingMore, setIsLoadingMore] = useState(false);
        const isLoadingMoreRef = useRef<boolean>(false);
        const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const wasPopoverOpenRef = useRef(false);
        const [expiryMonth, setExpiryMonth] = useState('');
        const [expiryYear, setExpiryYear] = useState('');
        const [cvv, setCvv] = useState('');
        const [updateCardStatus] = useUpdateCardStatusMutation();
        const [personalizePrepaidCard] = usePersonalizePrepaidCardMutation();
        const { currentUserState } = useSelector((state: RootState) => state?.currentUserState);
        const listUrl = `/Card/CustomerCardsV2?customerId=${currentUserState?.companyId}`;
        const [requestBody, setRequestBody] = useState({
            filters: cardFilters,
            pageSize: 25,
            page: 1,
            orderBy: '',
            orderDesc: false,
            customerId: currentUserState?.companyId || undefined,
        });

        const popupState = usePopupState({
            variant: 'popover',
            popupId: flowMode === 'add' ? 'card-add-popup' : 'card-activate-popup',
        });

        const toggleCard = (card: any) => {
            if (selectedCard?.cardNo === card.cardNo) {
                setSelectedCard(null);
            } else {
                setSelectedCard(card);
                popupState.close();
            }
        };

        const isCardSelected = (cardNo: string) => {
            return selectedCard?.cardNo === cardNo;
        };

        useEffect(() => {
            if (currentUserState?.companyId) {
                setRequestBody((prev) => ({ ...prev, customerId: currentUserState.companyId, filters: cardFilters }));
            }
        }, [currentUserState?.companyId]);

        const { data: fetchData, isLoading: isFetchLoading, isFetching, refetch } = useFetchDataNoCacheQuery(
            { url: listUrl, filter: requestBody, method: 'POST' },
            {
                skip: !requestBody?.customerId || !isActive,
                refetchOnMountOrArgChange: true,
            }
        );
        const isRequestLoading = isFetching || isFetchLoading;

        // Popover her açıldığında listeyi tazele (kapalı→açık geçişte; refetch referansı değişiminde döngü olmasın)
        useEffect(() => {
            const open = popupState.isOpen;
            if (!isActive || !requestBody?.customerId) {
                wasPopoverOpenRef.current = open;
                return;
            }
            if (open && !wasPopoverOpenRef.current) {
                void refetch();
            }
            wasPopoverOpenRef.current = open;
        }, [popupState.isOpen, isActive, requestBody?.customerId, refetch]);

        useEffect(() => {
            if (isActive && currentUserState?.companyId) {
                setCurrentPage(1);
                setAllCards([]);
                setHasMore(true);
                setSelectedCard(null);
                setCardSearchText('');
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                setRequestBody((prev) => ({
                    ...prev,
                    page: 1,
                    customerId: currentUserState?.companyId,
                    filters: cardFilters,
                }));
            }
        }, [isActive, currentUserState?.companyId]);

        useEffect(() => {
            if (requestBody?.page === 1 && isActive) {
                setAllCards([]);
            }
        }, [requestBody?.page, isActive]);

        useEffect(() => {
            if (fetchData && isActive) {
                const { rows: newCards, totalCount, totalPages, failed } = parseCustomerCardsV2Response(fetchData);

                if (failed) {
                    setHasMore(false);
                    setIsLoadingMore(false);
                    isLoadingMoreRef.current = false;
                    if (currentPage === 1) {
                        setAllCards([]);
                    }
                    return;
                }

                const pageSize = requestBody.pageSize ?? 25;

                if (newCards.length > 0) {
                    if (currentPage === 1) {
                        setAllCards(newCards);
                    } else {
                        setAllCards((prev) => {
                            const existingIds = new Set(prev.map((card: any) => card.id ?? card.cardNo));
                            const uniqueNewCards = newCards.filter((card: any) => !existingIds.has(card.id ?? card.cardNo));
                            return [...prev, ...uniqueNewCards];
                        });
                    }
                } else if (currentPage === 1) {
                    setAllCards([]);
                }

                if (typeof totalCount === 'number' && totalCount >= 0) {
                    setHasMore(currentPage * pageSize < totalCount);
                } else if (typeof totalPages === 'number' && totalPages >= 1) {
                    setHasMore(currentPage < totalPages);
                } else {
                    setHasMore(newCards.length >= pageSize);
                }

                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
            }
        }, [fetchData, currentPage, isActive, requestBody.pageSize]);

        useEffect(() => {
            if (!isActive) return;

            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }

            const trimmed = cardSearchText.trim();

            // Boş arama: debounce yok, listeyi silme — ilk açılışta kartlar görünsün; RTK aynı cevabı döndürse effect tetiklenmezdi
            if (trimmed === '') {
                setCurrentPage(1);
                setHasMore(true);
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                setRequestBody((prev) => ({
                    ...prev,
                    page: 1,
                    filters: [...cardFilters],
                }));
                return;
            }

            searchTimerRef.current = setTimeout(() => {
                const extraFilters = [
                    {
                        field: 'cardNo',
                        logicOperator: 'and',
                        values: [trimmed],
                        valueType: 1,
                        operationType: 1,
                    },
                ];

                setAllCards([]);
                setCurrentPage(1);
                setHasMore(true);
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                setRequestBody((prev) => ({
                    ...prev,
                    page: 1,
                    filters: [...cardFilters, ...extraFilters],
                }));
            }, 500);

            return () => {
                if (searchTimerRef.current) {
                    clearTimeout(searchTimerRef.current);
                }
            };
        }, [cardSearchText, isActive]);

        const loadNextPage = () => {
            if (!hasMore || isLoadingMore || isRequestLoading || isLoadingMoreRef.current) {
                return;
            }
            isLoadingMoreRef.current = true;
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            setRequestBody((prev) => ({ ...prev, page: nextPage }));
        };

        const trimmedCardSearch = cardSearchText.trim();

        const hasListMatchForSearch = useMemo(() => {
            if (trimmedCardSearch.length !== 16) return false;
            return allCards.some((c) => String(c.cardNo || '').trim() === trimmedCardSearch);
        }, [allCards, trimmedCardSearch]);

        const showManualCardSelect =
            flowMode === 'add' &&
            trimmedCardSearch.length === 16 &&
            !hasListMatchForSearch &&
            !isRequestLoading &&
            isActive;

        const handleSelectManualCard = useCallback(() => {
            const t = cardSearchText.trim();
            if (t.length !== 16) return;
            setSelectedCard({ cardNo: t });
            popupState.close();
        }, [cardSearchText, popupState]);

        const handleReset = useCallback(() => {
            setSelectedCard(null);
            setCardSearchText('');
            setCurrentPage(1);
            setAllCards([]);
            setHasMore(true);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody((prev) => ({ ...prev, page: 1 }));
            setExpiryMonth('');
            setExpiryYear('');
            setCvv('');
            popupState.close();
            onClose();
        }, [onClose, popupState]);

        const handleConfirm = useCallback(async () => {
            if (!selectedCard) {
                enqueueSnackbar('Lütfen kart seçiniz', {
                    variant: 'warning',
                });
                return;
            }

            if (!expiryMonth || !expiryYear) {
                enqueueSnackbar('Lütfen son kullanma tarihini seçiniz', {
                    variant: 'warning',
                });
                return;
            }

            if (!cvv || cvv.length !== 3) {
                enqueueSnackbar('Lütfen 3 haneli CVV kodunu giriniz', {
                    variant: 'warning',
                });
                return;
            }

            try {
                onLoadingChange?.(true);

                const companyId = currentUserState?.companyId || undefined;
                const expiryDatePlain = expiryYear + String(expiryMonth || '').padStart(2, '0');

                if (flowMode === 'add') {
                    const personalizePayload = {
                        CompanyId: companyId,
                        CardNo: encrypt(selectedCard.cardNo),
                        ExpiryDate: encrypt(expiryDatePlain),
                        Cvv: encrypt(cvv),
                    };

                    const response: any = await personalizePrepaidCard(personalizePayload);

                    if (response?.data?.success) {
                        enqueueSnackbar(response?.data?.message || 'İşlem başarılı', {
                            variant: 'success',
                        });
                        setSelectedCard(null);
                        setExpiryMonth('');
                        setExpiryYear('');
                        setCvv('');
                        onAddCardSuccess?.();
                        handleReset();
                    }
                } else {
                    const activationInfo = {
                        cardNo: encrypt(selectedCard.cardNo),
                        expiryDate: encrypt(expiryDatePlain),
                        cvv: encrypt(cvv),
                        companyId,
                    };

                    const response: any = await updateCardStatus(activationInfo);

                    if (response?.data?.success) {
                        enqueueSnackbar(response?.data?.message, {
                            variant: 'success',
                        });
                        setSelectedCard(null);
                        setExpiryMonth('');
                        setExpiryYear('');
                        setCvv('');
                        onActivateSucceeded?.();
                        handleReset();
                    }
                }
            } catch (error: any) {
                const errorMessage = error?.error?.message || error?.message || 'Bilinmeyen bir hata oluştu';
                enqueueSnackbar(errorMessage, { variant: 'error' });
            } finally {
                onLoadingChange?.(false);
            }
        }, [
            selectedCard,
            expiryMonth,
            expiryYear,
            cvv,
            currentUserState?.companyId,
            flowMode,
            updateCardStatus,
            personalizePrepaidCard,
            handleReset,
            onLoadingChange,
            onActivateSucceeded,
            onAddCardSuccess,
        ]);

        useImperativeHandle(
            ref,
            () => ({
                submit: () => {
                    void handleConfirm();
                },
                reset: () => {
                    handleReset();
                },
            }),
            [handleConfirm, handleReset]
        );

        const getAvailableMonths = () => {
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;

            if (!expiryYear || parseInt(expiryYear, 10) > currentYear) {
                return Array.from({ length: 12 }, (_, i) => i + 1);
            }
            if (parseInt(expiryYear, 10) === currentYear) {
                return Array.from({ length: 12 - currentMonth + 1 }, (_, i) => currentMonth + i);
            }
            return [];
        };

        const getAvailableYears = () => {
            const currentYear = new Date().getFullYear();
            return Array.from({ length: 6 }, (_, i) => currentYear + i);
        };

        const getMonthName = (month: number) => {
            const months = [
                'Ocak',
                'Şubat',
                'Mart',
                'Nisan',
                'Mayıs',
                'Haziran',
                'Temmuz',
                'Ağustos',
                'Eylül',
                'Ekim',
                'Kasım',
                'Aralık',
            ];
            return months[month - 1];
        };

        const introText =
            flowMode === 'add'
                ? 'Eklemek istediğiniz kartın bilgilerini giriniz:'
                : 'Aktif etmek istediğiniz kartı seçiniz:';

        return (
            <>
                <Typography variant="body1" sx={{ mb: 3, color: '#64748b' }}>
                    {introText}
                </Typography>

                <Box
                    {...bindTrigger(popupState)}
                    sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        minHeight: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        gap: 0.75,
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: 'primary.main',
                        },
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {!selectedCard ? (
                            <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                {flowMode === 'add' ? 'Kart numarası girin veya kartı seçin' : 'Kart arayın veya kartı seçin'}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    width: 'fit-content',
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: '1 1 auto', textAlign: 'center' }}>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e3c72',
                                            letterSpacing: '0.02em',
                                            lineHeight: 1.4,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {selectedCard.cardNo}
                                        {(selectedCard.embossName || selectedCard.cardName || selectedCard.customerName) && (
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontWeight: 500,
                                                    ml: 0.5,
                                                }}
                                            >
                                                - {selectedCard.embossName || selectedCard.cardName || selectedCard.customerName}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    aria-label="Kart seçimini kaldır"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        setSelectedCard(null);
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        width: 30,
                                        height: 30,
                                        p: 0,
                                        color: '#64748b',
                                        border: '1px solid transparent',
                                        borderRadius: '50%',
                                        transition: 'color 0.15s, border-color 0.15s, background-color 0.15s',
                                        '&:hover': {
                                            color: 'error.main',
                                            borderColor: 'rgba(239, 68, 68, 0.35)',
                                            bgcolor: 'rgba(239, 68, 68, 0.06)',
                                        },
                                    }}
                                >
                                    <Close sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {isRequestLoading && currentPage === 1 ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : null}
                        </Box>
                        <KeyboardArrowDown sx={{ color: 'text.secondary', flexShrink: 0 }} />
                    </Box>
                </Box>

                <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: { width: 500, mt: 1, maxHeight: 450, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                    }}
                >
                    <>
                        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Kart No ile ara..."
                                value={cardSearchText}
                                onChange={(e) => setCardSearchText(e.target.value)}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        showManualCardSelect || cardSearchText ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    maxWidth: showManualCardSelect ? '72%' : undefined,
                                                    flexWrap: 'nowrap',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {showManualCardSelect && (
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectManualCard();
                                                        }}
                                                        sx={{
                                                            minWidth: 0,
                                                            px: 0.75,
                                                            py: 0.25,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            whiteSpace: 'nowrap',
                                                            textTransform: 'none',
                                                            borderRadius: 1,
                                                            color: 'primary.main',
                                                            bgcolor: 'action.hover',
                                                            '&:hover': {
                                                                bgcolor: 'primary.main',
                                                                color: '#fff',
                                                            },
                                                        }}
                                                    >
                                                        Kartı Ekle
                                                    </Button>
                                                )}
                                                {cardSearchText ? (
                                                    <IconButton size="small" onClick={() => setCardSearchText('')} edge="end">
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                ) : null}
                                            </InputAdornment>
                                        ) : undefined,
                                }}
                            />
                        </Box>
                        <List
                            sx={{
                                flexGrow: 1,
                                overflowY: 'auto',
                                maxHeight: 300,
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#ccc',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#aaa',
                                },
                            }}
                            onScroll={(e) => {
                                const target = e.currentTarget;
                                if (isRequestLoading || isLoadingMore || !hasMore || isLoadingMoreRef.current) {
                                    return;
                                }
                                if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
                                    loadNextPage();
                                }
                            }}
                        >
                            {isRequestLoading && currentPage === 1 ? (
                                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <CircularProgress size={40} thickness={4} sx={{ color: '#3b82f6' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                        Kartlar Yükleniyor...
                                    </Typography>
                                </Box>
                            ) : allCards.length === 0 ? (
                                flowMode === 'add' && cardSearchText.trim() !== '' && !isRequestLoading && currentPage === 1 ? (
                                    <Box sx={{ py: 0.5 }} />
                                ) : (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Kart bulunamadı
                                        </Typography>
                                    </Box>
                                )
                            ) : (
                                allCards.map((card) => (
                                    <ListItem key={card.id ?? card.cardNo} button onClick={() => toggleCard(card)} sx={{ py: 0.5 }}>
                                        <Checkbox edge="start" checked={isCardSelected(card.cardNo)} tabIndex={-1} disableRipple />
                                        <ListItemText
                                            primary={card.cardNo}
                                            secondary={card.cardName || card.embossName || card.customerName}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem' } }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', ml: 'auto', pl: 2, fontWeight: 600, fontSize: '0.85rem' }}
                                        >
                                            {card.customerName || card.embossName || card.cardName}
                                        </Typography>
                                    </ListItem>
                                ))
                            )}
                            {(isLoadingMore || isLoadingMoreRef.current || (isRequestLoading && currentPage > 1)) && hasMore && (
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 2,
                                        background: 'linear-gradient(to bottom, transparent, rgba(59, 130, 246, 0.05))',
                                        borderTop: '1px solid #f1f5f9',
                                        width: '100%',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: 24,
                                            height: 24,
                                            flexShrink: 0,
                                            mb: 1,
                                        }}
                                    >
                                        <CircularProgress size={24} thickness={4} sx={{ color: '#e2e8f0' }} variant="determinate" value={100} />
                                        <CircularProgress
                                            size={24}
                                            thickness={4}
                                            sx={{
                                                color: '#3b82f6',
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                animationDuration: '800ms',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#3b82f6',
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                            fontSize: '0.65rem',
                                        }}
                                    >
                                        Kartlar Yükleniyor
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </>
                </Popover>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, fontSize:'1rem' }}>
                        Kart Bilgileri
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Son Kullanma Yılı</InputLabel>
                                <Select
                                    value={expiryYear}
                                    onChange={(e) => {
                                        setExpiryYear(e.target.value);
                                        setExpiryMonth('');
                                    }}
                                    label="Son Kullanma Yılı"
                                >
                                    {getAvailableYears().map((year) => (
                                        <MenuItem key={year} value={year.toString()}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Son Kullanma Ayı</InputLabel>
                                <Select
                                    value={expiryMonth}
                                    onChange={(e) => setExpiryMonth(e.target.value)}
                                    label="Son Kullanma Ayı"
                                    disabled={!expiryYear}
                                >
                                    {getAvailableMonths().map((month) => (
                                        <MenuItem key={month} value={month.toString()}>
                                            {month.toString().padStart(2, '0')} - {getMonthName(month)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="CVV"
                                value={cvv}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setCvv(value.slice(0, 3));
                                }}
                                placeholder="123"
                                inputProps={{
                                    maxLength: 3,
                                    style: { letterSpacing: '0.2em', textAlign: 'left' },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 1, display: 'block', fontSize: '0.875rem', lineHeight: 1.5 }}>
                        CVV kodu kartınızın arkasında bulunan 3 haneli güvenlik kodudur.
                    </Typography>
                </Box>
            </>
        );
    }
);
